import { useTranslation } from "react-i18next";
import { Drawer } from "../../../../components/Drawer";
import { Textarea } from "../../../../components/Textarea";
import { Info } from "../../../../components/Info";
import { DrawerDefaultsExtra } from "../../../tasks/components/DrawerDefaultsExtra";
import { Controller, useForm } from "react-hook-form";
import { TestIDs } from "../../../../tests/types/testIds";
import { useState } from "react";
import { Flex } from "../../../../components/Flex";
import { WaslaConversationCreateNoteDto } from "../../../../model/integration/dto/WaslaConversationCreateNoteDto";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";

type Props = {
  open: boolean;
  onClose: () => void;
  activeChatId: string;
};

export const WANotesDrawer: React.FC<Props> = ({
  activeChatId,
  open,
  onClose,
}) => {
  const [createNoteVisible, setCreateNoteVisible] = useState(false);
  const [createNoteLoading, setCreateNoteLoading] = useState(false);

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const whatsAppDirectIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppDirectIntegration,
  )!;

  const { handleSubmit, control, getValues } =
    useForm<WaslaConversationCreateNoteDto>();
  const { t } = useTranslation();

  const createNote = async () => {
    try {
      setCreateNoteLoading(true);
      await dispatch(
        integrationActions.createWhatsAppNote({
          chatId: activeChatId,
          integrationId: whatsAppDirectIntegration?._id!,
          note: getValues("note"),
          userId,
        }),
      ).unwrap();

      setCreateNoteVisible(false);
      AtiraToast.success(
        t("integration.whatsapp.direct.chat.notes.drawer.note.success"),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setCreateNoteLoading(false);
    }
  };

  return (
    <Drawer
      size={"large"}
      title={t("common.notes")}
      open={open}
      onClose={onClose}
      extra={
        <DrawerDefaultsExtra
          onSubmit={() => setCreateNoteVisible(true)}
          onCancel={onClose}
          // submitTestId={TestIDs.tasks.createEdit.drawer.button}
          config={{ loading: createNoteLoading, hideCancelButton: true }}
          submitTitle={t("common.add")}
        />
      }
    >
      <Info info={t("integration.whatsapp.direct.chat.notes.info")} />

      <Drawer
        open={createNoteVisible}
        title={t("integration.whatsapp.direct.chat.notes.drawer.note.title")}
        onClose={() => setCreateNoteVisible(false)}
        size={"large"}
        extra={
          <DrawerDefaultsExtra
            onSubmit={handleSubmit(createNote)}
            onCancel={() => setCreateNoteVisible(false)}
            // submitTestId={TestIDs.tasks.createEdit.drawer.button}
            config={{ loading: createNoteLoading }}
            submitTitle={t("common.create")}
          />
        }
      >
        <Flex>
          <Controller
            control={control}
            name="note"
            render={({ field: { onChange, value } }) => (
              <Textarea
                title={t(
                  "integration.whatsapp.direct.chat.notes.drawer.note.input.title",
                )}
                placeholder={t(
                  "integration.whatsapp.direct.chat.notes.drawer.note.input.placeholder",
                )}
                value={value}
                maxLength={Lengths.LONG}
                onChange={onChange}
                autoCorrect="on"
              />
            )}
          />
        </Flex>
      </Drawer>
    </Drawer>
  );
};
